import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Backdrop,
  Box,
  CircularProgress,
  Container,
  CssBaseline,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { v4 as uuidv4 } from "uuid";
import { createOrg, Org } from "../models/Org";
import { PageContext } from "../contexts/PageContext";
import PageHeader from "../components/PageHeader";
import Minibar from "../components/Minibar";
import { Save, Delete, Shuffle, AddCard } from "@mui/icons-material";
import { deleteRecord, loadRecord, saveRecord } from "../api/connection";
import { showConfirmDialog } from "../components/UniversalDialog";
import { User, UserRole } from "../models/User";
import { BlobOrgImage } from "../components/BlogOrgImage";
import { ConstantColorFactor } from "three";

const EditOrganizationPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<Org | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isLoggedIn, user, setUser, org, setOrg } = useContext(PageContext);

  const [errors, setErrors] = useState({
    name: "",
    description: "",
    token: "",
  });

  useEffect(() => {
    async function fetchOrganization(id: string) {
      try {
        if (id === "new") {
          setOrganization(createOrg());
        } else {
          const org = await loadRecord<Org>("organizations", id);
          if (!org) {
            throw new Error("Organization not found.");
          }
          setOrganization(org);
        }
      } catch (err) {
        setError("Failed to fetch organization data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    if (id) {
      fetchOrganization(id);
    }
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    updatedOrganization: Org
  ) => {
    const { name, value } = e.target;
    setOrganization({ ...updatedOrganization, [name]: value });
  };

  const saveOrg = async (record: Org) => {
    setLoading(true);
    if (!validateFields()) {
      return; // Stop if validation fails
    }

    try {
      const res = await saveRecord<Org>("organizations", record);
      navigate("/edit-org/" + record._id);
    } catch (err) {
      console.error(
        `Failed to ${record._id === "new" ? "save" : "update"} organization:`,
        err
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrganization = async (record: Org) => {
    try {
      const res = await deleteRecord<Org>("organizations", record);
      console.log("Delete Response:", res);
      navigate("/dashboard");
    } catch (err) {
      console.error("Failed to delete organization:", err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // console.log(`Changing ${name} to ${value}`);
    if (organization) {
      handleChange(e, organization);
    }
  };

  const handleDeleteClick = async () => {
    const yes = await showConfirmDialog(
      "CONFIRM DELETION",
      "Are you sure you want to delete this organization?"
    );
    if (yes && organization) {
      await handleDeleteOrganization(organization);
    }
  };

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  const validateFields = () => {
    const newErrors = {
      name: !organization?.name ? "Name is required" : "",
      description: !organization?.description ? "Description is required" : "",
      token: !organization?.token ? "Token is required" : "",
    };

    setErrors(newErrors);

    // If there are no errors, return true
    return Object.values(newErrors).every((error) => error === "");
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />

        {/* page header */}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            m: 0,
            backgroundColor: "#212121",
            color: "#ECECEC",
            minHeight: "100vh",
          }}
        >
          <Backdrop
            open={loading}
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          <PageHeader
            title={
              id === "new" ? "New Organization" : "Edit Organization Details"
            }
            rightComponent={[
              <Minibar
                minibarItems={
                  id === "new"
                    ? [
                        {
                          text: `Save New Organization`,
                          icon: <Save />,
                          onClick: () => organization && saveOrg(organization),
                        },
                      ]
                    : [
                        {
                          text: `Update Organization`,
                          icon: <Save />,
                          onClick: () => organization && saveOrg(organization),
                        },
                        {
                          text: "Delete Organization",
                          icon: <Delete />,
                          onClick: () => handleDeleteClick(),
                        },
                        ...(user?.role === UserRole.SystemAdmin
                          ? [
                              {
                                text: "Switch to this Organization",
                                icon: <Shuffle />,
                                onClick: async () => {
                                  if (organization?._id) {
                                    const yes = await showConfirmDialog(
                                      "Switch Organizations",
                                      `Switch to ${organization.name}?`
                                    );
                                    if (yes) {
                                      setLoading(true);
                                      try {
                                        if (user) {
                                          const res = await saveRecord<User>(
                                            "users",
                                            {
                                              ...user,
                                              orgId: organization._id,
                                            }
                                          );
                                          setUser({
                                            ...user,
                                            orgId: organization._id,
                                          });
                                          setOrg(organization);
                                        }
                                      } catch (err) {
                                        console.error(
                                          `Failed to switch organizations:`,
                                          err
                                        );
                                      } finally {
                                        window.location.reload();
                                        setLoading(false);
                                      }
                                    }
                                  }
                                },
                                disabled: organization?._id === user?.orgId,
                              },
                            ]
                          : []),
                      ]
                }
                barLength={3}
              />,
            ]}
            // breadcrumbElements={[
            //   {
            //     label: `Inventory`,
            //     href: "/portal/inventory",
            //   },
            //   {
            //     label: "Organizations",
            //     href: "/orgs",
            //   },
            //   {
            //     label:
            //       organization && id !== "new"
            //         ? organization.name
            //         : "New Organization",
            //     href: "",
            //   },
            // ]}
          />
          <Container maxWidth="xl" sx={{ pt: "2rem" }}>
            <Grid
              container
              spacing={3}
              justifyContent="center"
              alignItems="center"
            >
              <Grid size={12}>
                <Stack spacing={2}>
                  <TextField
                    label="Name"
                    name="name"
                    value={organization?.name || ""}
                    fullWidth
                    onChange={handleInputChange}
                    error={!!errors.name}
                    helperText={errors.name}
                  />
                  <TextField
                    label="Description"
                    name="description"
                    value={organization?.description || ""}
                    fullWidth
                    onChange={handleInputChange}
                    error={!!errors.description}
                    helperText={errors.description}
                    multiline
                  />
                  <TextField
                    label="Token"
                    name="token"
                    value={organization?.token || ""}
                    disabled={id !== "new"}
                    fullWidth
                    onChange={handleInputChange}
                    error={!!errors.token}
                    helperText={errors.token}
                    multiline
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip
                              title="Generate New Token"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                size="small"
                                edge="start"
                                sx={{ padding: 0 }}
                                disabled={id !== "new"}
                                onClick={() => {
                                  const newGUID = uuidv4();
                                  if (organization) {
                                    handleChange(
                                      {
                                        target: {
                                          name: "token",
                                          value: newGUID,
                                        },
                                      } as React.ChangeEvent<HTMLInputElement>,
                                      organization
                                    );
                                  }
                                }}
                              >
                                <AddCard />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <Typography variant="h6">Organization Banner</Typography>
                  <BlobOrgImage
                    style={{ maxWidth: 500 }}
                    blobId={organization?.imageBlobId || ""}
                    assigner={(blobId: string) => {
                      if (organization) {
                        console.log("Assigning new imageBlobId to organization:", blobId);
                        handleInputChange({
                          target: {
                            name: "imageBlobId",
                            value: blobId,
                          },
                        } as React.ChangeEvent<HTMLInputElement>);                          
                      }
                    }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default EditOrganizationPage;
